import type { ReactNode } from 'react'
import type { ScormData } from '~/utils/scorm'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'
import FinishedScreen from '~/pages/scorm/components/FinishedScreen'
import PlayerFrame from '~/pages/scorm/components/PlayerFrame'
import { createScorm12Adapter, ScormApiBridge } from '~/utils/scorm'

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    API_1484_11?: ScormApiBridge
    API?: ReturnType<typeof createScorm12Adapter>
  }
}

type ScormShellProps = {
  /** Chargement des données amont (queries) : affiche l'écran d'attente. */
  loading: boolean
  module?: { id: string, status: string, launchUrl?: string | null, title?: string | null } | null
  /** Tracking initial injecté dans le bridge (null = départ vierge). */
  initialData: ScormData | null
  /**
   * Persistance des commits SCORM. Doit être référentiellement stable
   * (useCallback ou constante module) : le bridge est remonté quand elle change.
   */
  onCommit: (data: ScormData) => Promise<void>
  /** Contenu additionnel du bandeau, à droite du nom de l'app. */
  headerContent?: ReactNode
  headerRight?: ReactNode
  /** Lien de repli affiché quand le module est indisponible. */
  fallbackTo: string
  fallbackLabel: string
  unavailableMessage: string
  /**
   * Vrai quand terminer CE module clôt le parcours entier : l'écran de fin
   * félicite alors pour la formation, pas pour une étape (#26). Laissé à
   * l'appelant, seul à savoir de quoi ce module fait partie — la
   * prévisualisation admin, elle, ne clôt jamais rien.
   */
  completesPath?: boolean
  /**
   * Ce qui remplace le contenu quand il rend la main (Terminate). Absent :
   * l'écran de fin de module. Un module à plusieurs SCO y met la fin du
   * chapitre et le suivant (#160).
   */
  renderFinished?: (info: { completed: boolean }) => ReactNode
  /** Le bridge monté, pour qu'un changement de chapitre le fasse terminer d'abord (#160). */
  bridgeRef?: { current: ScormApiBridge | null }
}

/**
 * Coquille commune du player SCORM (apprenant et prévisualisation) : vérifie
 * la disponibilité du contenu, monte les API SCORM 1.2/2004 dans window et
 * rend le bandeau + l'iframe (ou les écrans de chargement/indisponibilité).
 */
function ScormShell({
  loading,
  module,
  initialData,
  onCommit,
  headerContent,
  headerRight,
  fallbackTo,
  fallbackLabel,
  unavailableMessage,
  completesPath = false,
  renderFinished,
  bridgeRef,
}: ScormShellProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Renseigné quand le contenu appelle Terminate : le module a rendu la main
  // (son propre écran de fin dit « fermez la fenêtre », inutile en iframe).
  // On le remplace par notre écran de fin + redirection vers fallbackTo.
  const [finished, setFinished] = useState<{ completed: boolean } | null>(null)

  const canPlay = Boolean(module && module.status === 'READY' && module.launchUrl)
  const contentUrl = canPlay ? `/modules/${module!.id}/content/${module!.launchUrl}` : null

  // Garde-fou : vérifie que le contenu est réellement servi (fichiers présents)
  // avant d'afficher l'iframe, pour éviter d'exposer un JSON 404 brut.
  const [contentStatus, setContentStatus] = useState<'checking' | 'ok' | 'missing'>('checking')
  // La remise à « checking » suit le module affiché : elle se fait pendant le
  // rendu, pas dans l'effet. Posée dans l'effet, elle laissait peindre une image
  // avec le verdict du module PRÉCÉDENT — un « contenu indisponible » clignotant
  // au passage d'un module cassé au suivant.
  useSyncOnChange(contentUrl, () => setContentStatus('checking'))
  useEffect(() => {
    if (!contentUrl) return
    let cancelled = false
    fetch(contentUrl, { method: 'HEAD', credentials: 'include' })
      .then((res) => {
        if (!cancelled) setContentStatus(res.ok ? 'ok' : 'missing')
      })
      .catch(() => {
        if (!cancelled) setContentStatus('missing')
      })
    return () => { cancelled = true }
  }, [contentUrl])

  useEffect(() => {
    if (loading || !canPlay || contentStatus !== 'ok') return

    // Ignore le Terminate déclenché par le unload de l'iframe quand on quitte
    // nous-mêmes la page (bouton retour du bandeau, navigation…).
    let active = true
    const bridge = new ScormApiBridge(initialData, onCommit, (info) => {
      if (active) setFinished(info)
    })

    if (bridgeRef) bridgeRef.current = bridge
    // Mount SCORM 2004 API
    window.API_1484_11 = bridge
    // Mount SCORM 1.2 adapter
    window.API = createScorm12Adapter(bridge)

    // À la fermeture de l'onglet : les sauvegardes en file partent sur-le-champ,
    // puis on attend celles en vol pour leur donner une longueur d'avance avant
    // que le navigateur tue la page.
    const flushOnPageHide = () => {
      bridge.sendPendingNow()
      void bridge.flushCommits()
    }
    window.addEventListener('pagehide', flushOnPageHide)

    return () => {
      active = false
      if (bridgeRef?.current === bridge) bridgeRef.current = null
      window.removeEventListener('pagehide', flushOnPageHide)
      delete window.API_1484_11
      delete window.API
    }
  }, [loading, canPlay, contentStatus, initialData, onCommit, bridgeRef])

  if (loading) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-background">
        <Spinner className="size-6 text-muted-foreground" label={t('scorm.player.loading')} />
        <p aria-hidden className="font-heading text-lg tracking-wide">{t('scorm.player.loading')}</p>
      </div>
    )
  }

  return (
    <PlayerFrame
      fallbackTo={fallbackTo}
      fallbackLabel={fallbackLabel}
      headerContent={headerContent}
      headerRight={headerRight}
    >
      {!canPlay || contentStatus === 'missing'
        ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 bg-background px-6 text-center">
              <p className="text-lg">
                {contentStatus === 'missing'
                  ? t('scorm.player.contentError')
                  : unavailableMessage}
              </p>
              <Button variant="outline" onClick={() => navigate(fallbackTo)}>
                {fallbackLabel}
              </Button>
            </div>
          )
        : contentStatus === 'checking'
          ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 bg-background">
                <Spinner className="size-6 text-muted-foreground" label={t('scorm.player.loading')} />
                <p aria-hidden className="font-heading text-lg tracking-wide">{t('scorm.player.loading')}</p>
              </div>
            )
          : finished && renderFinished
            ? renderFinished(finished)
            : finished
              ? (
                  <FinishedScreen
                    success={finished.completed}
                    title={finished.completed
                      ? t(completesPath ? 'scorm.finished.pathCompletedTitle' : 'scorm.finished.completedTitle')
                      : t('scorm.finished.title')}
                  >
                    {finished.completed && completesPath && <p className="text-sm">{t('scorm.finished.pathCompletedHint')}</p>}
                    <Button className="mt-1" onClick={() => navigate(fallbackTo)}>{fallbackLabel}</Button>
                  </FinishedScreen>
                )
              : (
            /*
                    `allow-scripts` + `allow-same-origin` est signalé comme
                    dangereux, et l'est en général : ensemble, ils laissent le
                    contenu retirer son propre bac à sable.

                    C'est ici la condition de fonctionnement du SCORM. La norme
                    veut que le module remonte l'arbre des fenêtres jusqu'à
                    trouver `API` (SCORM 1.2) ou `API_1484_11` (2004) sur un
                    parent — un accès que `allow-same-origin` seul autorise, et
                    sans lequel aucune progression n'est enregistrée. Le contenu
                    est par ailleurs servi depuis notre origine, donc le bac à
                    sable ne le séparait déjà de rien.

                    La vraie réponse est de servir les modules depuis une origine
                    distincte, ce qui se décide côté infra et pas ici.
                  */
                  <iframe
                    src={contentUrl!}
                    // eslint-disable-next-line react-dom/no-unsafe-iframe-sandbox -- l'API SCORM exige l'accès au parent
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
                    className="w-full h-full border-0"
                    // Le nom du module, pas « Module SCORM » pour tous (RGAA 2.2).
                    title={module?.title ? t('scorm.player.titleNamed', { name: module.title }) : t('scorm.player.title')}
                    allow="fullscreen"
                  />
                )}
    </PlayerFrame>
  )
}

export default ScormShell
